import _ from "lodash";

import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import Button from "../../themed/button";
import Input from "../../themed/input/input";
import Select from "../../themed/select/select";

const pdoItemSchema = z.object({
    description: z.string().min(1, 'PDO description is required'),
    location: z.string().min(1, 'PDO location is required'),
    weight: z.coerce.number().positive(),
    height: z.coerce.number().positive(),
    threat: z.string().min(1, 'Threat is required'),
});

export type PDOItem = z.infer<typeof pdoItemSchema>;

type Props = {
    onSave: (info: PDOItem) => void;
    initialItem?: PDOItem;
};

const PDOItemForm: React.FC<Props> = ({ initialItem, onSave }) => {
    const form = useForm<PDOItem>({
        resolver: zodResolver(pdoItemSchema),
        mode: 'all',
        defaultValues: initialItem,
    });

    useEffect(() => {
        if (initialItem) form.reset(initialItem);
        else form.reset({ location: 'N/A', description: 'N/A', weight: 0, height: 0, threat: 'N/A' })
    }, [form, initialItem]);

    return (  
        <form onSubmit={form.handleSubmit(onSave)}>
            <div className="container">
                <div className="row">
                    <div className="col">
                        <Controller
                            control={form.control}
                            name='description'
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="PDO Description"
                                    placeholder="Enter PDO Description"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={form.formState.errors.description ? form.formState.errors.description.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={form.control}
                            name='location'
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="PDO Location"
                                    placeholder="Enter PDO Location"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={form.formState.errors.location ? form.formState.errors.location.message : ''}
                                />
                            )}
                        />
                    </div>
                </div>
                
                <div className="divider my-4"></div>

                <div className="row">
                    <div className="col">
                        <Controller
                            control={form.control}
                            name="height"
                            render={({ field }) => (
                                <Select
                                    options={HEIGHTS}
                                    label="Height (M) (Approx.)"
                                    placeholder="Enter Height (M)"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={form.formState.errors.height ? form.formState.errors.height.message : ''}
                                />
                            )}
                        />
                    </div>

                    <div className="col">
                        <Controller
                            control={form.control}
                            name="weight"
                            render={({ field }) => (
                                <Select
                                    options={WEIGHTS}
                                    label="Weight (KG) (Approx.)"
                                    placeholder="Enter Weight (KG)"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={form.formState.errors.weight ? form.formState.errors.weight.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                        <div className="col">
                        <Controller
                            control={form.control}
                            name="threat"
                            render={({ field }) => (
                                <Select
                                    label="Threat To"
                                    placeholder="Select Threat To"
                                    options={THREATS}
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={form.formState.errors.threat ? form.formState.errors.threat.message : ''}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="divider my-4"></div>

            <div className="d-flex justify-content-end">
                <Button
                    type="submit"
                    disabled={!form.formState.isValid}
                    label={form.formState.isSubmitting ? "Saving Information..." : "Save Information"}
                />
            </div>
        </form>
    );
};

export const HEIGHTS = _.range(0.5, 1000, 0.5).map((fill) => ({
    id: fill,
    name: fill,
}));

export const WEIGHTS = _.range(0.5, 500, 0.5).map((fill) => ({
    id: fill,
    name: fill,
}));

export const THREATS = [
    {
        id: 'HUMAN',
        name: 'HUMAN',
    },
    {
        id: 'ASSET',
        name: 'ASSET',
    },
    {
        id: 'HUMAN AND ASSET',
        name: 'HUMAN AND ASSET',
    },
];
 
export default PDOItemForm;